import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LogOut, Mail, MessageSquare, Users, Loader2, Printer, Send } from "lucide-react";

export default function Admin() {
  const { user, login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await http.post("/api/auth/admin/login", { email, password });
      login(res.data.token, res.data.user);
      toast.success("Signed in");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally { setBusy(false); }
  }

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white rounded-2xl border border-[#0E2A47]/8 p-10 shadow-[0_4px_40px_rgba(14,42,71,0.06)]">
          <Link to="/" className="text-xs uppercase tracking-widest text-[#0E2A47]/50 font-body hover:text-[#1FB6C9]">← Home</Link>
          <h1 className="mt-6 font-display text-3xl text-[#0E2A47]">Owner sign in</h1>
          <p className="mt-2 text-sm text-[#0E2A47]/60 font-body">Admin access to subscribers, inquiries, and stats.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#0E2A47]/50 font-body">Email</span>
              <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-lg border border-[#0E2A47]/15 bg-white px-4 py-3 outline-none focus:border-[#1FB6C9]" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#0E2A47]/50 font-body">Password</span>
              <input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-lg border border-[#0E2A47]/15 bg-white px-4 py-3 outline-none focus:border-[#1FB6C9]" />
            </label>
            <button data-testid="admin-submit" type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0E2A47] text-white py-3.5 font-display disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }
  return <Dashboard onLogout={() => { logout(); nav("/"); }} />;
}

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [inq, setInq] = useState([]);
  const [tab, setTab] = useState("subs");
  const [dripBusy, setDripBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      http.get("/api/admin/stats"),
      http.get("/api/admin/subscribers"),
      http.get("/api/inquiries"),
    ]).then(([a, b, c]) => { setStats(a.data); setSubs(b.data.subscribers); setInq(c.data); })
      .catch(() => toast.error("Failed to load dashboard"));
  }, []);

  async function runDripNow() {
    if (!window.confirm("Send day-3 and day-10 drip emails to all subscribers who are due? This sends real email.")) return;
    setDripBusy(true);
    try {
      const res = await http.post("/api/admin/drip/run-now");
      toast.success(`Drip sent: ${res.data.day3} day-3 · ${res.data.day10} day-10 · ${res.data.errors} errors`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Drip run failed");
    } finally { setDripBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <header className="border-b border-[#0E2A47]/8 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display font-semibold text-[#0E2A47]">Grandview · Owner</div>
          <div className="flex items-center gap-4">
            <Link to="/admin/content" data-testid="admin-content-link" className="inline-flex items-center gap-1.5 text-sm text-[#0E2A47]/70 hover:text-[#0E2A47] font-body"><Send size={14} /> Content editor</Link>
            <Link to="/admin/poster" data-testid="admin-poster-link" className="inline-flex items-center gap-1.5 text-sm text-[#0E2A47]/70 hover:text-[#0E2A47] font-body"><Printer size={14} /> QR poster</Link>
            <button data-testid="admin-run-drip" onClick={runDripNow} disabled={dripBusy} className="inline-flex items-center gap-1.5 text-sm text-[#0E2A47]/70 hover:text-[#0E2A47] font-body disabled:opacity-40">
              {dripBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Run drip now
            </button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-1.5 text-sm text-[#0E2A47]/60 hover:text-[#0E2A47] font-body"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={Users} label="Subscribers" value={stats?.subscribers} />
          <Stat icon={Mail} label="Opted in to drip" value={stats?.opt_in_drip} />
          <Stat icon={MessageSquare} label="Inquiries" value={stats?.inquiries} />
          <Stat icon={MessageSquare} label="Concierge chats" value={stats?.concierge_messages} />
        </div>

        <div className="mt-8 flex gap-2 border-b border-[#0E2A47]/10">
          <TabBtn active={tab === "subs"} onClick={() => setTab("subs")} testid="admin-tab-subs">Subscribers</TabBtn>
          <TabBtn active={tab === "inq"} onClick={() => setTab("inq")} testid="admin-tab-inq">Inquiries</TabBtn>
        </div>

        <div className="mt-6">
          {tab === "subs" ? <SubsTable rows={subs} /> : <InqTable rows={inq} />}
        </div>
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl bg-white border border-[#0E2A47]/8 p-4">
    <div className="flex items-center gap-2 text-[#0E2A47]/50 text-xs uppercase tracking-widest font-body"><Icon size={14} /> {label}</div>
    <div className="mt-2 font-display text-3xl text-[#0E2A47]">{value ?? "—"}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`px-4 py-3 -mb-px border-b-2 font-display text-sm ${active ? "border-[#1FB6C9] text-[#0E2A47]" : "border-transparent text-[#0E2A47]/50 hover:text-[#0E2A47]"}`}>{children}</button>
);

function SubsTable({ rows }) {
  if (rows.length === 0) return <Empty label="No subscribers yet." />;
  return (
    <div data-testid="subs-table" className="rounded-xl bg-white border border-[#0E2A47]/8 overflow-hidden">
      <table className="w-full text-sm font-body">
        <thead className="bg-[#F1E8DA]/40 text-[#0E2A47]/60 uppercase text-xs tracking-widest">
          <tr><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Last name</th><th className="text-left px-5 py-3">Arrival</th><th className="text-left px-5 py-3">Departure</th><th className="text-left px-5 py-3">Opt-in</th><th className="text-left px-5 py-3">Joined</th></tr>
        </thead>
        <tbody className="divide-y divide-[#0E2A47]/6">
          {rows.map((r) => (
            <tr key={r.email} data-testid={`sub-row-${r.email}`}>
              <td className="px-5 py-3 text-[#0E2A47]">{r.email}</td>
              <td className="px-5 py-3 text-[#0E2A47]/80">{r.last_name || "—"}</td>
              <td className="px-5 py-3 text-[#0E2A47]/70">{r.check_in || "—"}</td>
              <td className="px-5 py-3 text-[#0E2A47]/70">{r.check_out || "—"}</td>
              <td className="px-5 py-3">{r.opt_in_drip ? <span className="rounded-full bg-[#1FB6C9]/15 text-[#0E2A47] text-xs px-2 py-0.5">Yes</span> : "—"}</td>
              <td className="px-5 py-3 text-[#0E2A47]/70">{r.created_at?.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InqTable({ rows }) {
  if (rows.length === 0) return <Empty label="No inquiries yet." />;
  return (
    <div data-testid="inq-table" className="rounded-xl bg-white border border-[#0E2A47]/8 overflow-hidden">
      <table className="w-full text-sm font-body">
        <thead className="bg-[#F1E8DA]/40 text-[#0E2A47]/60 uppercase text-xs tracking-widest">
          <tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Dates</th><th className="text-left px-5 py-3">Group</th><th className="text-left px-5 py-3">Purpose</th></tr>
        </thead>
        <tbody className="divide-y divide-[#0E2A47]/6">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`inq-row-${r.id}`}>
              <td className="px-5 py-3 text-[#0E2A47]">{r.name}</td>
              <td className="px-5 py-3 text-[#0E2A47]/70">{r.email}</td>
              <td className="px-5 py-3 text-[#0E2A47]/70">{[r.check_in, r.check_out].filter(Boolean).join(" → ") || "—"}</td>
              <td className="px-5 py-3 text-[#0E2A47]/70">{r.group_size || "—"}</td>
              <td className="px-5 py-3 text-[#0E2A47]/70">{r.purpose || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Empty = ({ label }) => <div className="rounded-xl bg-white border border-[#0E2A47]/8 p-10 text-center text-[#0E2A47]/60 font-body">{label}</div>;
